'use client';

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { Star, MapPin, BadgeCheck, ArrowRight, Zap, Crown } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

const FEATURED_PROVIDERS = [
{
  id: 'prov-001',
  slug: 'james-hartley-plumbing',
  name: 'James Hartley',
  trade: 'Plumber & Gas Engineer',
  location: 'Clapham, SW4',
  rating: 4.9,
  reviewCount: 214,
  tier: 'premium' as const,
  tagline: 'Emergency callouts covered. Gas Safe registered.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_139e4448e-1772451223398.png",
  avatarAlt: 'James Hartley, smiling male plumber in blue workwear',
  verified: true,
  specialisms: ['Boiler service', 'Emergency repairs', 'Gas Safe']
},
{
  id: 'prov-002',
  slug: 'sarah-chen-electrical',
  name: 'Sarah Chen Electrical',
  trade: 'Electrician',
  location: 'Shoreditch, E1',
  rating: 4.8,
  reviewCount: 187,
  tier: 'pro' as const,
  tagline: 'NICEIC approved. Domestic & commercial.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfeb2e53-1772209907496.png",
  avatarAlt: 'Sarah Chen, female electrician with safety helmet',
  verified: true,
  specialisms: ['Consumer units', 'EV chargers', 'NICEIC']
},
{
  id: 'prov-003',
  slug: 'mike-okafor-builders',
  name: 'Okafor Building Co.',
  trade: 'Builder & Extensions',
  location: 'Hackney, E8',
  rating: 4.7,
  reviewCount: 143,
  tier: 'pro' as const,
  tagline: 'Extensions, loft conversions, full refurbs.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1291a0ceb-1772142540350.png",
  avatarAlt: 'Mike Okafor, builder wearing hard hat on construction site',
  verified: true,
  specialisms: ['Extensions', 'Loft conversions', 'Refurbs']
},
{
  id: 'prov-004',
  slug: 'green-leaf-landscaping',
  name: 'Green Leaf Landscapes',
  trade: 'Landscaper & Garden Design',
  location: 'Richmond, TW9',
  rating: 4.9,
  reviewCount: 98,
  tier: 'premium' as const,
  tagline: 'Award-winning garden design. Fully insured.',
  avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_180e774f0-1772069778651.png',
  avatarAlt: 'Emma Walsh, female landscaper standing in a garden',
  verified: true,
  specialisms: ['Garden design', 'Decking', 'Planting']
},
{
  id: 'prov-005',
  slug: 'northern-gas-solutions',
  name: 'Northern Gas Solutions',
  trade: 'Gas Engineer',
  location: 'Didsbury, M20',
  rating: 4.8,
  reviewCount: 321,
  tier: 'premium' as const,
  tagline: "Manchester's top-rated gas engineer. 24/7.",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16a177863-1773054346187.png",
  avatarAlt: 'Raj Patel, gas engineer in yellow safety vest',
  verified: true,
  specialisms: ['Boiler install', 'Gas Safety certs', '24/7']
},
{
  id: 'prov-006',
  slug: 'prestige-decorators-london',
  name: 'Prestige Decorators',
  trade: 'Painter & Decorator',
  location: 'Notting Hill, W11',
  rating: 4.6,
  reviewCount: 76,
  tier: 'free' as const,
  tagline: 'Immaculate finish guaranteed. Fully insured.',
  avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a3cc914b-1764705370275.png',
  avatarAlt: 'Tom Bradley, decorator holding a paint roller',
  verified: false,
  specialisms: ['Interior painting', 'Wallpaper', 'Exterior']
}];


const TierBadge = ({ tier }: {tier: 'free' | 'pro' | 'premium';}) => {
  if (tier === 'premium') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tier-premium">
        <Crown size={10} />
        Premium
      </span>);

  }
  if (tier === 'pro') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tier-pro">
        <Zap size={10} />
        Pro
      </span>);

  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tier-free">
      Free
    </span>);

};

export default function FeaturedProviders() {
  return (
    <section className="py-12 md:py-16 relative" id="providers-section">
      {/* Section background with tiled geometric pattern */}
      <div className="absolute inset-0">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url("https://lovelocals.co.uk/assets/hero-geometric-BCt4JoXz.jpg")`,
            backgroundSize: '800px',
            backgroundRepeat: 'repeat'
          }} />

        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(248,245,250,0.95)' }} />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              Featured Professionals
            </h2>
            <p className="text-gray-500">{FEATURED_PROVIDERS.length} providers found</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FEATURED_PROVIDERS.map((provider) =>
          <Link
            key={provider.id}
            href="/public-provider-profile"
            className="group bg-white rounded-2xl p-5 border border-gray-200 hover:border-love-mint/50 hover:shadow-lg transition-all duration-300"
            style={{ '--hover-shadow': '0 8px 30px rgba(37,12,49,0.05)' } as React.CSSProperties}>

              <div className="flex gap-4">
                <div className="relative flex-shrink-0">
                  <div
                  className="h-16 w-16 rounded-xl border-2 border-gray-200 overflow-hidden group-hover:border-love-mint/30 transition-colors"
                  style={{ borderColor: 'rgba(229,231,235,1)' }}>

                    <AppImage
                    src={provider.avatar}
                    alt={provider.avatarAlt}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover" />

                  </div>
                  {provider.verified &&
                <div
                  className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#99E6C8' }}>

                      <BadgeCheck size={12} style={{ color: '#250C31' }} />
                    </div>
                }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3
                      className="font-semibold text-gray-900 truncate transition-colors"
                      style={{ '--hover-color': '#250C31' } as React.CSSProperties}>

                        {provider.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-500">{provider.trade}</span>
                      </div>
                    </div>
                    <TierBadge tier={provider.tier} />
                  </div>

                  <div className="flex items-center gap-1 mt-1 text-gray-400 text-xs">
                    <MapPin size={11} />
                    <span>{provider.location}</span>
                  </div>
                </div>
              </div>

              {/* Specialisms */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {provider.specialisms.map((spec) =>
              <span
                key={`${provider.id}-spec-${spec}`}
                className="inline-flex px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-500">

                    {spec}
                  </span>
              )}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Star size={13} className="text-amber-400 fill-amber-400" />
                  <span className="font-semibold text-sm text-gray-800">{provider.rating}</span>
                  <span className="text-gray-400 text-xs">({provider.reviewCount})</span>
                </div>
                <button
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200"
                style={{ backgroundColor: '#250C31', color: '#99E6C8' }}>

                  View Profile
                </button>
              </div>
            </Link>
          )}
        </div>

        <div className="text-center mt-8">
          <Link
            href="/provider-directory"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-gray-200 bg-white text-gray-700 text-base font-medium hover:bg-gray-50 hover:border-gray-300 transition-all">

            Load More
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>);

}